import {
  ButtonERP,
  ButtonFilterERP,
  CalendarPrimeReact,
  SearchInput,
} from "@finanedi.es/storybook"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  exportRemesasToExcel,
  FacturaCarteraQueryParamsProps,
} from "api/Factura.service"
import { SortingCriteria } from "api/interfaces/SortingCriteria"
import { DATE_YYYY_MM_DD } from "common/components/FilterMenu/constants"
import { handleDirectDownloadExcel } from "common/context/DownloadFileProvider"
import { PAYMENT_TYPE } from "common/utils/constants"
import { formatDate } from "common/utils/utils"
import { AnimatePresence } from "framer-motion"
import { Dialog } from "primereact/dialog"
import { InputNumber } from "primereact/inputnumber"
import { Menu } from "primereact/menu"
import { Dispatch, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { backendDateFormat } from "utils/formatDate"
import { getFileNameFromContentDisposition } from "utils/getFileNameFromContentDisposition"
import { PAYMENT_TYPE_CARTERA_ACTION_SETTINGS } from "./constants"

export const FilterMenu = ({
  serviceQuery,
  sortingCriteria,
  skipCount,
  maxResultCount,
  resetPagination,
  handleDates,
  handleTable,
  handleTabChangeResetSelection,
}: {
  serviceQuery: Dispatch<Partial<FacturaCarteraQueryParamsProps>>
  sortingCriteria?: SortingCriteria
  skipCount?: FacturaCarteraQueryParamsProps["SkipCount"]
  maxResultCount?: FacturaCarteraQueryParamsProps["MaxResultCount"]
  resetPagination?: () => void
  handleDates: {
    dates: Date[] | undefined
    setDates: React.Dispatch<React.SetStateAction<Date[] | undefined>>
  }
  handleTable: {
    tabSelected: number
    setTabSelected: React.Dispatch<React.SetStateAction<number>>
  }
  handleTabChangeResetSelection: () => void
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const [menuOpen, setMenuOpen] = useState(false)
  const [keyword, setKeyword] = useState<string | undefined>()
  const [amount, setAmount] = useState<Record<string, number | undefined>>({
    min: undefined,
    max: undefined,
  })

  const menu = useRef<Menu>(null)
  const openFilters = () => {
    setMenuOpen(true)
  }

  const queryFixer = (
    queryParams: FacturaCarteraQueryParamsProps
  ): FacturaCarteraQueryParamsProps => {
    const facturasParams = {
      FechaLowerLimit: queryParams.FechaLowerLimit,
      FechaUpperLimit: queryParams.FechaUpperLimit,
      ImporteLowerLimit: queryParams.ImporteLowerLimit,
      ImporteUpperLimit: queryParams.ImporteUpperLimit,
      Keyword: queryParams.Keyword,
      MaxResultCount: queryParams.MaxResultCount,
      SkipCount: queryParams.SkipCount,
      SortingCriteria:
        queryParams.SortingCriteria?.property &&
        queryParams.SortingCriteria?.sort
          ? `${queryParams.SortingCriteria.property} ${queryParams.SortingCriteria.sort}`
          : "CreationTime DESC",
    }
    const fixedQueryParams = Object.fromEntries(
      Object.entries(facturasParams).filter(
        ([_key, value]) => value || value === 0 // eslint-disable-line @typescript-eslint/no-unused-vars
      )
    )
    return fixedQueryParams
  }

  const query = {
    FechaLowerLimit:
      handleDates?.dates && handleDates?.dates[0]
        ? formatDate(handleDates?.dates[0], DATE_YYYY_MM_DD)
        : "",
    FechaUpperLimit:
      handleDates?.dates && handleDates?.dates[1]
        ? formatDate(handleDates?.dates[1], DATE_YYYY_MM_DD)
        : "",
    ImporteLowerLimit: amount.min,
    ImporteUpperLimit: amount.max,
    Keyword: keyword,
    MaxResultCount: maxResultCount,
    SkipCount: skipCount,
    SortingCriteria: sortingCriteria,
  }

  const handleQuery = () => {
    serviceQuery(queryFixer(query))
  }
  const handleApplyFilters = () => {
    setMenuOpen(false)
  }
  const hideFilter = () => {
    setMenuOpen(!menuOpen)
  }
  const handleResetFilters = () => {
    setAmount({
      min: undefined,
      max: undefined,
    })
    // serviceQuery(query)
  }

  const appliedFiltersTags = [
    {
      label: t("filtros.appliedTag.amountFrom"),
      value: amount?.min
        ? Intl.NumberFormat("de-DE", {
            style: "currency",
            currency: "EUR",
          }).format(amount?.min)
        : undefined,
      action: () => setAmount({ ...amount, min: undefined }),
    },
    {
      label: t("filtros.appliedTag.amountTo"),
      value: amount?.max
        ? Intl.NumberFormat("de-DE", {
            style: "currency",
            currency: "EUR",
          }).format(amount?.max)
        : undefined,
      action: () => setAmount({ ...amount, max: undefined }),
    },
  ]
  const appliedFilters = appliedFiltersTags.filter((filter) => filter.value)

  useEffect(() => {
    resetPagination?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    keyword,
    handleDates?.dates,
    appliedFilters?.length,
    handleTable?.tabSelected,
  ])

  useEffect(() => {
    handleQuery()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    handleDates?.dates,
    appliedFilters?.length,
    amount?.min,
    amount?.max,
    keyword,
    sortingCriteria?.sort,
    sortingCriteria?.property,
    skipCount,
    maxResultCount,
  ])

  return (
    <>
      <div className='!tw-bg-white !tw-px-6 tw-flex tw-flex-col xl:tw-flex-row tw-justify-between tw-items-center tw-pb-4 tw-pt-6 tw-shadow-3xl tw-rounded-t-xl mb-1'>
        <p className='tw-whitespace-nowrap tw-text-2xl tw-text-text tw-my-auto'>
          {t("cartera.title")}
        </p>
        <div className='tw-flex tw-w-full tw-justify-center xl:tw-justify-end'>
          <div className='!tw-w-fit tw-flex tw-items-center !tw-flex-row tw-justify-end tw-flex-wrap'>
            <div className='tw-grid tw-gap-3 2xl:tw-flex 2xl:tw-flex-row md:tw-grid-cols-2 tw-items-center tw-w-fit'>
              <ButtonFilterERP
                isActive={appliedFilters.length > 0}
                numberActive={appliedFilters.length}
                onClick={openFilters}
                deleteFilter={handleResetFilters}
                filterButtonLabel={`${t("filtros.title")}`}
              />

              <div className='calendar-container tw-relative !tw-mx-0 '>
                {handleDates?.dates && (
                  <FontAwesomeIcon
                    icon={["fal", "calendar-lines"]}
                    className='!tw-absolute tw-text-simple-10 tw-top-2 tw-left-6 tw-h-5 tw-z-10 tw-cursor-pointer tw-pt-0.5'
                  />
                )}
                <CalendarPrimeReact
                  value={handleDates?.dates}
                  selectionMode='range'
                  locale={language}
                  dateFormat='dd/mm/yy'
                  className={`${
                    handleDates?.dates && "calendar-selected"
                  } !tw-w-full`}
                  onChange={({ value }) => {
                    if (value) {
                      handleDates?.setDates(value as Date[])
                      resetPagination?.()
                    }
                  }}
                  readOnlyInput
                  showIcon={!handleDates?.dates}
                />

                {handleDates?.dates &&
                  handleDates?.dates?.some(
                    (date: Date | undefined) => date !== undefined
                  ) && (
                    <FontAwesomeIcon
                      icon={["fal", "xmark"]}
                      className='!tw-absolute tw-top-2 tw-right-6 tw-text-simple-10 tw-h-4 tw-w-4 tw-cursor-pointer !tw-pt-1'
                      onClick={() => {
                        handleDates?.setDates(undefined)
                        resetPagination?.()
                      }}
                    />
                  )}
              </div>
              <SearchInput
                placeholder={`${t("validaciones.placeholderSearch")}`}
                onChange={(ev: string) => {
                  resetPagination?.()
                  setKeyword(ev)
                }}
                inputValue={keyword}
                className='tw-mb-1 sm:tw-mb-0 tw-h-10 tw-w-full 2xl:tw-w-72'
              />
              <span className='tw-flex tw-w-full md:tw-w-fit tw-relative tw-gap-2 tw-items-center'>
                <div className='tw-grid tw-grid-cols-2 tw-gap-1 tw-h-12 tw-w-auto tw-p-1 tw-bg-neutral-10 tw-rounded tw-cursor-pointer'>
                  <span
                    key='pagossCartera'
                    className={`tw-px-6 tw-flex tw-items-center tw-h-10 tw-font-medium tw-rounded tw-text-nowrap ${
                      handleTable?.tabSelected === PAYMENT_TYPE.PAGO
                        ? "tw-bg-white tw-shadow"
                        : ""
                    }`}
                    onClick={() => {
                      handleTable?.tabSelected !== PAYMENT_TYPE.PAGO &&
                        handleTable?.setTabSelected(PAYMENT_TYPE.PAGO)
                      handleTabChangeResetSelection()
                    }}
                  >
                    {`${PAYMENT_TYPE_CARTERA_ACTION_SETTINGS(t)[2].label}s`}
                  </span>
                  <span
                    key='cobrosCartera'
                    className={`tw-px-6 tw-flex tw-items-center tw-h-10 tw-font-medium tw-rounded tw-text-nowrap ${
                      handleTable?.tabSelected === PAYMENT_TYPE.COBRO
                        ? "tw-bg-white tw-shadow"
                        : ""
                    }`}
                    onClick={() => {
                      handleTable?.tabSelected !== PAYMENT_TYPE.COBRO &&
                        handleTable?.setTabSelected(PAYMENT_TYPE.COBRO)
                      handleTabChangeResetSelection()
                    }}
                  >
                    {`${PAYMENT_TYPE_CARTERA_ACTION_SETTINGS(t)[1].label}s`}
                  </span>
                </div>
                <div className='tw-grid tw-place-content-center tw-border-solid tw-border tw-rounded-md tw-size-10 hover:!tw-bg-gray-100 tw-select-none'>
                  <Menu
                    popup
                    ref={menu}
                    id='popup_menu_remesa_detail'
                    className='!tw-py-1'
                    model={[
                      {
                        label: `${t("mayor.exportButton")}`,
                        icon: "pi pi-file-excel",
                        command: async () => {
                          const res = await exportRemesasToExcel({
                            FechaLowerLimit: handleDates?.dates?.[0]
                              ? backendDateFormat(handleDates?.dates[0])
                              : undefined,
                            FechaUpperLimit: handleDates?.dates?.[1]
                              ? backendDateFormat(handleDates?.dates[1])
                              : undefined,
                            TipoCartera: handleTable?.tabSelected,
                          })
                          const fileName =
                            getFileNameFromContentDisposition(
                              res.headers["content-disposition"]
                            ) ?? "Cartera.xlsx"
                          handleDirectDownloadExcel(res.data, fileName)
                        },
                      },
                    ]}
                  />
                  <div
                    className='tw-grid tw-place-content-center tw-cursor-pointer tw-size-10 tw-rounded-md'
                    onClick={(event) => menu.current?.toggle(event)}
                    aria-controls='popup_menu_remesa_detail'
                    aria-haspopup
                  >
                    <FontAwesomeIcon
                      icon={["fal", "ellipsis-v"]}
                      aria-controls='popup_menu_remesa_detail'
                      aria-haspopup
                      className='tw-text-black tw-size-5'
                    />
                  </div>
                </div>
              </span>
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        <Dialog
          breakpoints={{ "960px": "75vw" }}
          className='p-dialog-right tw-select-none'
          header={t("filtros.title")}
          visible={menuOpen}
          footer={
            <div className='tw-w-full tw-border-t-2 tw-pt-6'>
              <ButtonERP
                customize={{
                  type: "primary",
                  size: "md",
                }}
                className='!tw-w-full'
                onClick={handleApplyFilters}
                label={`${t("filtros.labelApply")}`}
              />
            </div>
          }
          onHide={() => {
            hideFilter()
          }}
          draggable={false}
          resizable={false}
          position='right'
          closable
          closeOnEscape
        >
          {!!appliedFilters?.length && (
            <div className='tw-mb-8'>
              <div className='tw-flex tw-mb-4 tw-justify-between'>
                <span className='tw-font-medium'>
                  {t("tickets.filter.appliedFilters")}
                </span>
                <span
                  onClick={handleResetFilters}
                  className='tw-text-text-hyperlinks tw-cursor-pointer'
                >
                  {t("tickets.filter.deleteAll")}
                </span>
              </div>
              <div className='tw-flex tw-flex-wrap tw-mb-8 tw-gap-2 tw-font-normal tw-text-xs'>
                {appliedFilters.map((filter) => (
                  <div
                    key={filter.label}
                    onClick={filter.action}
                    className='tw-items-center tw-justify-between tw-cursor-pointer tw-bg-info-10 tw-text-info tw-rounded-md tw-border tw-border-info tw-py-1 tw-px-2'
                  >
                    <span>
                      {filter.label} {filter.value}
                    </span>
                    <FontAwesomeIcon
                      icon={["fal", "xmark"]}
                      className='tw-ml-2 tw-text-info'
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className='tw-mb-8'>
            <div className='tw-mb-4'>
              <span className='tw-font-medium'>
                {t("filtros.importe.title")}
              </span>
            </div>
            <div className='tw-flex tw-flex-row tw-mb-8'>
              <div className='tw-flex tw-flex-col'>
                <label>{`${t("filtros.importe.fromLabel")} (€)`}</label>
                <InputNumber
                  className='[&_input]:!tw-w-36 !tw-w-36'
                  value={amount?.min}
                  placeholder='0,00 €'
                  onValueChange={(e) => setAmount({ ...amount, min: e.value })}
                  minFractionDigits={2}
                  min={0}
                  max={amount?.max ? amount.max - 1 : Number.MAX_SAFE_INTEGER}
                  mode='currency'
                  currency='EUR'
                />
              </div>
              <div className='tw-py-3 tw-px-6 tw-items-baseline tw-mt-auto tw-mb-0'>
                -
              </div>
              <div className='tw-flex tw-flex-col'>
                <label>{`${t("filtros.importe.toLabel")} (€)`}</label>
                <InputNumber
                  className='[&_input]:!tw-w-36 !tw-w-36'
                  value={amount?.max}
                  placeholder='0,00 €'
                  onValueChange={(e) => {
                    setAmount({ ...amount, max: e.value })
                  }}
                  minFractionDigits={2}
                  min={amount?.min ?? 0}
                  max={Number.MAX_SAFE_INTEGER}
                  mode='currency'
                  currency='EUR'
                />
              </div>
            </div>
          </div>
        </Dialog>
      </AnimatePresence>
    </>
  )
}
